import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.section`
  padding: 5rem 5%;
  background-color: ${({ theme }) => theme.colors.surface};
  text-align: center;
  scroll-margin-top: 100px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1rem;

  span {
    position: relative;
    z-index: 1;
    &::after {
      content: '';
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 10px;
      background-color: ${({ theme }) => theme.colors.accentTeal}60;
      z-index: -1;
    }
  }
`;

const Subtitle = styled.p`
  max-width: 820px;
  margin: 0 auto 2.5rem;
  color: ${({ theme }) => theme.colors.light};
  line-height: 1.6;
`;

// --- TOGGLE SWITCH ---
const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
`;

const Label = styled.span`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ active, theme }) => (active ? theme.colors.secondary : theme.colors.light)};
  cursor: pointer;
  transition: color 0.3s;
`;

const Switch = styled.div`
  position: relative;
  width: 56px;
  height: 30px;
  background: ${({ theme }) => theme.colors.accentTeal};
  border-radius: 99px;
  cursor: pointer;
  padding: 4px;
  transition: background 0.3s;

  &::after {
    content: '';
    position: absolute;
    left: ${({ isAnnual }) => (isAnnual ? 'calc(100% - 26px)' : '4px')};
    top: 4px;
    width: 22px;
    height: 22px;
    background: #fff;
    border-radius: 50%;
    transition: left 0.3s cubic-bezier(0.4, 0.03, 0, 1);
  }
`;

const DiscountBadge = styled.span`
  background: ${({ theme }) => theme.colors.accentOrange}20;
  color: ${({ theme }) => theme.colors.accentOrange};
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  margin-left: -0.5rem;
`;

// --- GRID & CARDS (Design Original Restaurado) ---
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background};
  padding: 2rem;
  border-radius: 16px;
  text-align: left;
  border: 1px solid ${({ isFeatured, theme }) => (isFeatured ? theme.colors.accentTeal : theme.colors.background)};
  transition: transform 0.2s ease, border-color 0.2s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: ${({ isFeatured }) => (isFeatured ? '0 10px 30px -10px rgba(0,0,0,0.1)' : 'none')};

  &:hover {
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.accentOrange};
  }
`;

const PlanNameRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const PlanName = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const Pill = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: ${({ theme, highlight }) => highlight ? theme.colors.accentTeal : theme.colors.accentTeal + '25'};
  color: ${({ theme, highlight }) => highlight ? '#fff' : theme.colors.secondary};
  border: 1px solid ${({ theme, highlight }) => highlight ? 'transparent' : theme.colors.accentTeal + '40'};
  white-space: nowrap;
`;

const PriceContainer = styled.div`
  margin: 1rem 0 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 80px;
`;

const PriceLabel = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.light};
  margin-bottom: 0.25rem;
  display: block;
`;

const PriceValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;

  strong {
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1;
    color: ${({ theme }) => theme.colors.secondary};
  }

  span {
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.light};
    font-weight: 400;
  }
`;

const PeriodNote = styled.span`
  display: block;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.accentTeal};
  margin-top: 4px;
  font-weight: 500;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  flex-grow: 1;

  li {
    margin-bottom: 0.6rem;
    color: ${({ theme }) => theme.colors.light};
    line-height: 1.4;
    /* Removido o display:flex para evitar o espaçamento extra depois do strong */
  }
`;

const ComingSoon = styled.span`
  margin-left: 0.5rem;
  font-size: 0.75rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.accentOrange}20;
  color: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.accentOrange}35;
  white-space: nowrap;
`;

// --- FAQ SIMPLIFICADO ---
const FAQContainer = styled.div`
  max-width: 700px;
  margin: 5rem auto 0;
  text-align: left;
`;

const FAQItem = styled.details`
  background: ${({ theme }) => theme.colors.background};
  margin-bottom: 1rem;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid transparent;

  &:hover {
    border-color: ${({ theme }) => theme.colors.surface};
  }

  summary {
    padding: 1.25rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.secondary};
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &::after {
      content: '+';
      font-size: 1.5rem;
      font-weight: 300;
      color: ${({ theme }) => theme.colors.accentTeal};
    }
  }

  &[open] summary::after {
    content: '-';
  }

  div {
    padding: 0 1.25rem 1.25rem;
    color: ${({ theme }) => theme.colors.light};
    line-height: 1.6;
    font-size: 0.95rem;
  }
`;

const PricingSection = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  // Helper simples para alternar preço
  const getPrice = (monthly, annual) => isAnnual ? annual : monthly;

  return (
    <Container id="planos">
      <SectionTitle>
        Planos de <span>Social Listening</span>
      </SectionTitle>
      <Subtitle>
        Transparência para você escolher o que faz sentido hoje. Alguns recursos avançados
        estão em desenvolvimento e aparecem marcados como <strong>Em breve</strong>.
      </Subtitle>

      <ToggleContainer>
        <Label active={!isAnnual} onClick={() => setIsAnnual(false)}>Mensal</Label>
        <Switch isAnnual={isAnnual} onClick={() => setIsAnnual(!isAnnual)} />
        <Label active={isAnnual} onClick={() => setIsAnnual(true)}>
          Anual
        </Label>
        {isAnnual && <DiscountBadge>-20% OFF</DiscountBadge>}
      </ToggleContainer>

      <Grid>
        {/* Sonda */}
        <Card>
          <PlanNameRow>
            <PlanName>Sonda</PlanName>
            <Pill>Entrada</Pill>
          </PlanNameRow>

          <PriceContainer>
            <PriceLabel>A partir de</PriceLabel>
            <PriceValueRow>
              <strong>R$ {getPrice(189, 149)}</strong>
              <span>/mês</span>
            </PriceValueRow>
            {isAnnual && <PeriodNote>Cobrado anualmente</PeriodNote>}
          </PriceContainer>

          <List>
            <li><strong>1.500</strong> comentários/mês</li>
            <li><strong>1</strong> perfil conectado</li>
            <li><strong>60 dias</strong> de histórico</li>
            <li>Sentimento Automático (IA)</li>
            <li><strong>10</strong> keywords monitoradas</li>
            <li>Exportação CSV</li>
          </List>
        </Card>

        {/* Radar */}
        <Card isFeatured={true}>
          <PlanNameRow>
            <PlanName>Radar</PlanName>
            <Pill highlight={true}>Recomendado</Pill>
          </PlanNameRow>

          <PriceContainer>
            <PriceLabel>A partir de</PriceLabel>
            <PriceValueRow>
              <strong>R$ {getPrice(429, 349)}</strong>
              <span>/mês</span>
            </PriceValueRow>
            {isAnnual && <PeriodNote>Cobrado anualmente</PeriodNote>}
          </PriceContainer>

          <List>
            <li><strong>6.000</strong> comentários/mês</li>
            <li><strong>3</strong> perfis conectados</li>
            <li><strong>6 meses</strong> de histórico</li>
            <li>Sentimento Automático (IA)</li>
            <li><strong>30</strong> keywords monitoradas</li>
            <li>Exportação CSV</li>
            <li>
              Classificações avançadas
              <ComingSoon>Em breve</ComingSoon>
            </li>
          </List>
        </Card>

        {/* Observatório */}
        <Card>
          <PlanNameRow>
            <PlanName>Observatório</PlanName>
            <Pill>Agências</Pill>
          </PlanNameRow>

          <PriceContainer>
            <PriceLabel>A partir de</PriceLabel>
            <PriceValueRow>
              <strong>R$ {getPrice(949, 799)}</strong>
              <span>/mês</span>
            </PriceValueRow>
            {isAnnual && <PeriodNote>Cobrado anualmente</PeriodNote>}
          </PriceContainer>

          <List>
            <li><strong>15.000</strong> comentários/mês</li>
            <li><strong>10</strong> perfis conectados</li>
            <li><strong>12 meses</strong> de histórico</li>
            <li>Sentimento Automático (IA)</li>
            <li><strong>100</strong> keywords monitoradas</li>
            <li>Relatórios Customizados (Sob Demanda)</li>
            <li>
              Classificações avançadas
              <ComingSoon>Em breve</ComingSoon>
            </li>
            <li>
              PDF White Label
              <ComingSoon>Em breve</ComingSoon>
            </li>
          </List>
        </Card>

        <Card>
          <PlanNameRow>
            <PlanName>Enterprise</PlanName>
            <Pill>Sob consulta</Pill>
          </PlanNameRow>

          <PriceContainer>
            <PriceLabel>&nbsp;</PriceLabel>
            <PriceValueRow>
              <strong>Custom</strong>
            </PriceValueRow>
          </PriceContainer>

          <List>
            <li>Volume de comentários e perfis <strong>sob medida</strong></li>
            <li><strong>SLA</strong> e suporte dedicado</li>
            <li>Relatórios Customizados (Sob Demanda)</li>
            <li>Backfill histórico<ComingSoon>Em breve</ComingSoon></li>
            <li>Classificações personalizadas<ComingSoon>Em breve</ComingSoon></li>
          </List>
        </Card>
      </Grid>

      {/* FAQ DE EXPANSÃO */}
      <FAQContainer>
        <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', fontSize: '1.1rem', color: '#888' }}>
          Perguntas Frequentes
        </h3>
        
        <FAQItem>
          <summary>O que acontece se eu atingir o limite de comentários?</summary>
          <div>
            Seu monitoramento não para! Você pode adquirir <strong>Pacotes de Volume</strong> avulsos 
            diretamente no painel. O custo por comentário nesses pacotes diminui progressivamente: 
            quanto maior o pacote, menor o valor unitário.
          </div>
        </FAQItem>

        <FAQItem>
          <summary>Posso adicionar perfis extras?</summary>
          <div>
            Sim. Se precisar monitorar apenas um perfil a mais do que seu plano permite, 
            você pode contratar um slot extra individualmente, sem a necessidade de migrar 
            imediatamente para o plano superior.
          </div>
        </FAQItem>

        <FAQItem>
          <summary>Como funciona o pagamento anual?</summary>
          <div>
            Ao optar pelo plano anual, você garante um desconto de aproximadamente 20% em comparação 
            ao valor mensal recorrente. O pagamento é realizado em uma única cobrança no início do ciclo.
          </div>
        </FAQItem>

        <FAQItem>
          <summary>Vocês fazem relatórios manuais?</summary>
          <div>
            Sim! Clientes dos planos Observatório e Enterprise podem solicitar relatórios de inteligência 
            sob demanda (cobrados à parte). Nossa equipe de analistas prepara um material aprofundado 
            com insights qualitativos para suas reuniões estratégicas.
          </div>
        </FAQItem>
      </FAQContainer>
    </Container>
  );
};

export default PricingSection;